import { useEffect, useState } from "react";
import { formatTigerDateTime, formatTigerDate } from "../../utils/tigerTime";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import { PickerKey, pickerInitialDir, rememberPickerDir } from "../../utils/pickerFolder";
import { useProgressPoll } from "../../utils/useProgressPoll";
import { setCdromPromptOpen } from "../../utils/cdromPrompt";
import { getReplayServerSession } from "../../utils/viewMode";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

type DataRequestKind = "compare" | "trend" | "query_diag" | "replay";

interface DataRequest {
  kind: DataRequestKind;
  config: string;
  turbine: string;
  start: number;
  end: number;
  samplerate: number;
  skipValid: number;
}

/** Raw backend payloads (snake_case from Rust). */
interface ComparePayload {
  config: string;
  turbine: string;
  start: number;
  end: number;
  skip_valid: number;
}

/** Parked CD-ROM request polled from the webserver (`/api/get_cdrom_request`).
 *  Mirrors the Rust `CdromRequest`; `kind` is the DataRequestKind discriminator. */
interface WebCdromRequest {
  kind: DataRequestKind;
  config: string;
  turbine: string;
  start: number;
  end: number;
  samplerate: number;
  skip_valid: number;
}
interface TrendLikePayload extends ComparePayload {
  samplerate: number;
}

/** Per-kind resource ids + command names. */
const KIND_META: Record<
  DataRequestKind,
  {
    event: string;
    titleId: string;
    downloadId: string;
    skipCmd: string;
    loadCmd: string;
    downloadCmd: string;
    cancelCmd: string;
    /** seconds to add for the "Skip to" label: +1s compare, +1 day others. */
    skipDelta: number;
    /** date-only display for trend/query-diag, datetime for compare. */
    dateOnly: boolean;
  }
> = {
  compare: {
    event: "compare-cdrom-request",
    titleId: "TIGER_TDATAREQ_DATA_TITLE",
    downloadId: "TIGER_TDATAREQ_DATA_DOWNLOAD",
    skipCmd: "compare_skip",
    loadCmd: "compare_load_cdrom",
    downloadCmd: "compare_download",
    cancelCmd: "compare_cancel",
    skipDelta: 1,
    dateOnly: false,
  },
  trend: {
    event: "trend-cdrom-request",
    titleId: "TIGER_TDATAREQ_TREND_TITLE",
    downloadId: "TIGER_TDATAREQ_TREND_DOWNLOAD",
    skipCmd: "trend_skip",
    loadCmd: "trend_load_cdrom",
    downloadCmd: "trend_download",
    cancelCmd: "trend_cancel",
    skipDelta: 86_400,
    dateOnly: true,
  },
  query_diag: {
    event: "query-diag-cdrom-request",
    titleId: "TIGER_TDATAREQ_DIAG_TITLE",
    downloadId: "TIGER_TDATAREQ_DIAG_DOWNLOAD",
    skipCmd: "query_diag_skip",
    loadCmd: "query_diag_load_cdrom",
    downloadCmd: "query_diag_download",
    cancelCmd: "query_diag_cancel",
    skipDelta: 86_400,
    dateOnly: true,
  },
  // Replay shares the compare "Data" wording and datetime display; only the
  // backend commands differ (the replay data-CDROM path in download.rs).
  replay: {
    event: "replay-cdrom-request",
    titleId: "TIGER_TDATAREQ_DATA_TITLE",
    downloadId: "TIGER_TDATAREQ_DATA_DOWNLOAD",
    skipCmd: "replay_skip",
    loadCmd: "replay_load_cdrom",
    downloadCmd: "replay_download",
    cancelCmd: "replay_cancel",
    skipDelta: 1,
    dateOnly: false,
  },
};

/** Per-config key for the data/CD-ROM Browse picker's session folder memory. */
const browseKey = (config: string) => `${PickerKey.dataBrowse}:${config}`;

/**
 * Faithful port of the C++ `TDataRequestShell_class` "Data Request" dialog.
 * Shown when a historical compare/trend/query-diag range — or a replay window —
 * isn't available and automatic download is off. Listens for all four backend
 * `*-cdrom-request` events; only needs to be mounted once. Skip / Browse /
 * Download / Cancel each drive the kind-prefixed Tauri command.
 *
 * Mounted only in the main window (App.tsx). The parked CD-ROM request is
 * per-session and the web tool windows share that session, so a request initiated
 * from a graph/diagnostics tool window surfaces here in the main window — the
 * single, expected place for the prompt.
 */
export function DataRequestDialog() {
  const [req, setReq] = useState<DataRequest | null>(null);
  // Browse picks a folder on THIS machine, which only makes sense when the data
  // path is local: on replay_server_session (broker) hosts the server does the
  // loading, so Browse is hidden — same reasoning as the Backup/Restore and
  // Advanced menu gates in MenuBar. Assume broker (hidden) until the flag loads.
  const [serverReplay, setServerReplay] = useState(true);

  useEffect(() => {
    if (!isTauri()) return; // web never shows Browse; skip the fetch
    let cancelled = false;
    void getReplayServerSession().then((v) => {
      if (!cancelled) setServerReplay(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];

    void import("@tauri-apps/api/event").then(({ listen }) => {
      const attach = <P,>(event: string, toReq: (p: P) => DataRequest) =>
        listen<P>(event, (e) => setReq(toReq(e.payload))).then((off) => {
          if (cancelled) off();
          else offs.push(off);
        });

      void attach<ComparePayload>(KIND_META.compare.event, (p) => ({
        kind: "compare",
        config: p.config,
        turbine: p.turbine,
        start: p.start,
        end: p.end,
        samplerate: 0,
        skipValid: p.skip_valid,
      }));
      void attach<TrendLikePayload>(KIND_META.trend.event, (p) => ({
        kind: "trend",
        config: p.config,
        turbine: p.turbine,
        start: p.start,
        end: p.end,
        samplerate: p.samplerate,
        skipValid: p.skip_valid,
      }));
      void attach<TrendLikePayload>(KIND_META.query_diag.event, (p) => ({
        kind: "query_diag",
        config: p.config,
        turbine: p.turbine,
        start: p.start,
        end: p.end,
        samplerate: p.samplerate,
        skipValid: p.skip_valid,
      }));
      void attach<ComparePayload>(KIND_META.replay.event, (p) => ({
        kind: "replay",
        config: p.config,
        turbine: p.turbine,
        start: p.start,
        end: p.end,
        samplerate: 0,
        skipValid: p.skip_valid,
      }));
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  // Web: no event channel — poll the parked CD-ROM request and mirror it into
  // `req`. The webserver parks one request at a time (the data fetch serialises
  // them); a null poll means nothing is pending, so the dialog closes.
  const webCdrom = useProgressPoll<WebCdromRequest>(!isTauri() ? "/api/get_cdrom_request" : null);
  useEffect(() => {
    if (isTauri()) return;
    setReq(
      webCdrom
        ? {
            kind: webCdrom.kind,
            config: webCdrom.config,
            turbine: webCdrom.turbine,
            start: webCdrom.start,
            end: webCdrom.end,
            samplerate: webCdrom.samplerate,
            skipValid: webCdrom.skip_valid,
          }
        : null,
    );
  }, [webCdrom]);

  // Progress overlays (HistoricalProgressDialog, DownloadProgressDialog) mount
  // after this dialog in App.tsx on the same overlay z-index, so they would
  // paint over the prompt and make its options unclickable. Signal them to
  // hide while an answer is pending; they reappear once an option is chosen.
  const promptOpen = req !== null;
  useEffect(() => {
    setCdromPromptOpen(promptOpen);
    return () => setCdromPromptOpen(false);
  }, [promptOpen]);

  if (!req) return null;

  const meta = KIND_META[req.kind];
  const fmt = (secs: number) => (meta.dateOnly ? formatTigerDate(secs) : formatTigerDateTime(secs));
  const startStr = fmt(req.start);
  const endStr = fmt(req.end);

  const title = tigerFormat(getTigerResource(meta.titleId), req.turbine);
  const message = tigerFormat(
    getTigerResource("TIGER_TDATAREQ_MSG"),
    startStr,
    endStr,
    req.turbine,
  );
  const skipLabel =
    req.skipValid === 0
      ? ""
      : tigerFormat(getTigerResource("TIGER_TDATAREQ_SKIP_TO"), fmt(req.end + meta.skipDelta));
  const browseLabel = getTigerResource("TIGER_TDATAREQ_BROWSE");
  const downloadLabel = tigerFormat(getTigerResource(meta.downloadId), startStr, endStr, req.turbine);

  const invokeCmd = async (cmd: string, args: Record<string, unknown>) => {
    if (!isTauri()) return;
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke(cmd, args);
  };

  // Web answers go to the park-and-poll endpoint; desktop uses the per-kind cmd.
  const answerWeb = async (action: "skip" | "download" | "cancel") => {
    await fetch(
      `/api/answer_cdrom_request?kind=${encodeURIComponent(req.kind)}&action=${action}&config_name=${encodeURIComponent(req.config)}`,
    );
  };

  const onSkip = async () => {
    if (isTauri()) await invokeCmd(meta.skipCmd, { configName: req.config });
    else await answerWeb("skip");
    setReq(null);
  };
  const onDownload = async () => {
    if (isTauri()) await invokeCmd(meta.downloadCmd, { configName: req.config });
    else await answerWeb("download");
    setReq(null);
  };
  const onCancel = async () => {
    if (isTauri()) await invokeCmd(meta.cancelCmd, { configName: req.config });
    else await answerWeb("cancel");
    setReq(null);
  };
  const onBrowse = async () => {
    if (!isTauri()) return;
    const { open } = await import("@tauri-apps/plugin-dialog");
    // Pre-fill with the last location loaded for this turbine this session, else
    // the turbine's default data folder (C++ seeds from the data dir and resets
    // the remembered location on restart).
    const chosen = await open({
      directory: true,
      defaultPath: await pickerInitialDir(browseKey(req.config), req.config),
    });
    if (typeof chosen === "string") {
      rememberPickerDir(browseKey(req.config), chosen, true);
      await invokeCmd(meta.loadCmd, { configName: req.config, dataLocation: chosen });
      setReq(null);
    }
    // picker cancelled → leave the dialog open
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button aria-label={getTigerResource("TIGER_RES_CLOSE")} className="acrylic-close-btn" onClick={onCancel}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <p style={styles.message}>{message}</p>
          <div style={styles.card}>
            <div style={styles.row}>
              <button
                className="btn-acrylic-secondary"
                style={styles.actionButton}
                onClick={onSkip}
                disabled={req.skipValid === 0}
              >
                {getTigerResource("TIGER_RES_SKIP")}
              </button>
              <span style={styles.rowLabel}>{skipLabel}</span>
            </div>
            {/* Browse loads from a local CD-ROM/folder path — meaningless in a
                browser (no server-side picker) and on broker
                (replay_server_session) hosts, so it's desktop-non-broker-only. */}
            {isTauri() && !serverReplay && (
              <div style={styles.row}>
                <button
                  className="btn-acrylic-secondary"
                  style={styles.actionButton}
                  onClick={onBrowse}
                >
                  {getTigerResource("TIGER_RES_BROWSE")}
                </button>
                <span style={styles.rowLabel}>{browseLabel}</span>
              </div>
            )}
            <div style={styles.row}>
              <button
                className="btn-acrylic-primary"
                style={styles.actionButton}
                onClick={onDownload}
              >
                {getTigerResource("TIGER_RES_DOWNLOAD")}
              </button>
              <span style={styles.rowLabel}>{downloadLabel}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" style={styles.actionButton} onClick={onCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  // This dialog is a plain flex-centred overlay card (not absolute-positioned/
  // resizable), so it keeps its own centering mechanics and only the surface is
  // restyled to the acrylic look.
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 460,
    maxWidth: "95vw",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  // Bespoke keys not provided by acrylicStyles.
  message: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: acrylicTokens.textSecondary,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  // Skip/Browse/Download/Cancel share one width so the option rows line up
  // (the base classes size to their label, leaving Download visibly wider).
  actionButton: {
    minWidth: 110,
    flexShrink: 0,
  },
  rowLabel: {
    flex: 1,
    fontSize: 13,
    color: acrylicTokens.textSecondary,
    whiteSpace: "pre-line" as const,
  },
} as const;
